import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { StatusDot } from "@/components/brand/status-dot.tsx";
import type { WorkspaceState } from "./workspace-state.ts";

function memoryValue(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value) ?? "-";
}

export function WorkPanel({ employeeName, state, loading, error, onRefresh }: {
  employeeName: string;
  state: WorkspaceState | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <Card id="saved-work" className="min-w-0" aria-labelledby="saved-work-heading">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <CardTitle><h2 id="saved-work-heading">Saved work</h2></CardTitle>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}><RefreshCw /> Refresh saved work</Button>
        </div>
        <CardDescription>Tasks and memory available to {employeeName}. Ask for changes in your conversation.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6" aria-busy={loading}>
        {loading ? <p role="status" className="text-sm text-muted-foreground">Loading saved work...</p>
          : error ? <Alert variant="destructive">
            <AlertDescription className="flex flex-col items-start gap-4"><p>{error}</p><Button variant="outline" onClick={onRefresh}>Try again</Button></AlertDescription>
          </Alert>
          : state ? <>
            <section aria-labelledby="work-tasks-heading" className="flex flex-col gap-3">
              <h3 id="work-tasks-heading" className="font-medium">Tasks <span className="text-muted-foreground">({state.tasks.length})</span></h3>
              {state.tasks.length === 0
                ? <p className="text-sm text-muted-foreground">No saved tasks yet. Saved tasks will appear here when available.</p>
                : <ul className="divide-y divide-border">
                  {state.tasks.map((task, index) => <li key={task.id ?? `${task.title}-${index}`} className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="min-w-0 break-words font-medium">{task.title}</p>
                      <StatusDot label={task.status} tone={task.status === "done" ? "success" : task.status === "blocked" ? "warning" : "neutral"} />
                    </div>
                    {task.summary && <p className="text-sm break-words whitespace-pre-wrap text-muted-foreground">{task.summary}</p>}
                  </li>)}
                </ul>}
            </section>
            <section aria-labelledby="work-memory-heading" className="flex flex-col gap-3 border-t border-border pt-6">
              <div>
                <h3 id="work-memory-heading" className="font-medium">Memory <span className="text-muted-foreground">({state.facts.length})</span></h3>
                <p className="mt-1 text-xs text-muted-foreground">May include shared workspace memory, not only this employee's notes.</p>
              </div>
              {state.facts.length === 0
                ? <p className="text-sm text-muted-foreground">Nothing saved yet. Tell {employeeName} something you want remembered.</p>
                : <dl className="flex flex-col gap-4">
                  {state.facts.map((fact) => <div key={fact.key} className="flex flex-col gap-1">
                    <dt className="text-xs break-words text-muted-foreground">{fact.key.replaceAll("_", " ")}</dt>
                    <dd className="text-sm break-words whitespace-pre-wrap">{memoryValue(fact.value)}</dd>
                    {fact.tags.length > 0 && <dd className="mt-1 flex flex-wrap gap-1">{fact.tags.map((tag) => <Badge key={tag} variant="secondary" className="max-w-full break-all whitespace-normal">{tag}</Badge>)}</dd>}
                  </div>)}
                </dl>}
            </section>
          </> : <p className="text-sm text-muted-foreground">Select an employee to see saved work.</p>}
      </CardContent>
    </Card>
  );
}
